import React, { useState } from "react";
import AssetUploadPanel from "./AssetUploadPanel";

export default function SignatureCaseEditor({
    draft,
    onChange,
    onSave,
    onReset,
    onDelete,
    onDuplicate,
    saving,
    hasChanges,
    supabaseEnabled,
}) {
    const [isUploadPanelOpen, setUploadPanelOpen] = useState(false);

    if (!draft) {
        return (
            <aside className="signature-editor">
                <div className="signature-editor__empty">
                    Sol taraftan bir proje seçin veya yeni bir proje oluşturun.
                </div>
            </aside>
        );
    }

    const handleFieldChange = (field, value) => {
        onChange({ ...draft, [field]: value });
    };

    const handleMetaChange = (index, value) => {
        const next = [...(draft.meta ?? [])];
        next[index] = value;
        onChange({ ...draft, meta: next });
    };

    const addMeta = () => {
        onChange({ ...draft, meta: [...(draft.meta ?? []), ""] });
    };

    const removeMeta = (index) => {
        const next = (draft.meta ?? []).filter((_, idx) => idx !== index);
        onChange({ ...draft, meta: next });
    };

    const handleBodyChange = (index, value) => {
        const next = [...(draft.body ?? [])];
        next[index] = value;
        onChange({ ...draft, body: next });
    };

    const addBody = () => {
        onChange({ ...draft, body: [...(draft.body ?? []), ""] });
    };

    const removeBody = (index) => {
        const next = (draft.body ?? []).filter((_, idx) => idx !== index);
        onChange({ ...draft, body: next });
    };

    const handleTimelineChange = (index, key, value) => {
        const next = [...(draft.timeline ?? [])];
        next[index] = { ...next[index], [key]: value };
        onChange({ ...draft, timeline: next });
    };

    const addTimeline = () => {
        onChange({ ...draft, timeline: [...(draft.timeline ?? []), { title: "", description: "" }] });
    };

    const removeTimeline = (index) => {
        const next = (draft.timeline ?? []).filter((_, idx) => idx !== index);
        onChange({ ...draft, timeline: next });
    };

    const handleGalleryChange = (index, key, value) => {
        const next = [...(draft.gallery ?? [])];
        next[index] = { ...next[index], [key]: value };
        onChange({ ...draft, gallery: next });
    };

    const addGallery = () => {
        onChange({ ...draft, gallery: [...(draft.gallery ?? []), { src: "", alt: "" }] });
    };

    const removeGallery = (index) => {
        const next = (draft.gallery ?? []).filter((_, idx) => idx !== index);
        onChange({ ...draft, gallery: next });
    };

    const handleInsertAsset = (url, alt) => {
        if (!url?.trim()) {
            return;
        }
        const next = [...(draft.gallery ?? []), { src: url.trim(), alt: alt?.trim() ?? "" }];
        onChange({ ...draft, gallery: next });
        setUploadPanelOpen(false);
    };

    return (
        <aside className="signature-editor" aria-label="Signature proje düzenleyici">
            <header className="signature-editor__header">
                <div>
                    <h3 className="signature-editor__title">Proje detayları</h3>
                    <p className="signature-editor__status">
                        {draft.published ? "Yayında" : "Taslak"} • ID: {draft.id}
                    </p>
                </div>
                <button
                    type="button"
                    className="signature-editor__button"
                    onClick={() => onDelete(draft.id)}
                    disabled={saving}
                >
                    Sil
                </button>
            </header>

            <div className="signature-editor__preview">
                {draft.gallery?.[0]?.src ? (
                    <img src={draft.gallery[0].src} alt={draft.title} />
                ) : (
                    <div className="signature-editor__empty">Kapak görseli ekleyin</div>
                )}
                <span className="signature-editor__preview-badge">Kapak</span>
                <span className="signature-editor__preview-title">{draft.title || "Başlık girin"}</span>
            </div>

            <div className="signature-editor__grid">
                <fieldset className="signature-editor__fieldset">
                    <legend>Genel bilgiler</legend>

                    <div className="signature-editor__row">
                        <label className="signature-editor__label" htmlFor="signature-title">
                            Başlık
                        </label>
                        <input
                            id="signature-title"
                            className="signature-editor__input"
                            value={draft.title}
                            onChange={(event) => handleFieldChange("title", event.target.value)}
                            placeholder="Örn. Harborfront Cultural Pavilion"
                        />
                    </div>

                    <div className="signature-editor__row">
                        <label className="signature-editor__label" htmlFor="signature-slug">
                            Slug (URL)
                        </label>
                        <input
                            id="signature-slug"
                            className="signature-editor__input"
                            value={draft.slug}
                            onChange={(event) => handleFieldChange("slug", event.target.value)}
                            placeholder="harborfront-cultural-pavilion"
                        />
                    </div>

                    <div className="signature-editor__row">
                        <label className="signature-editor__label" htmlFor="signature-intro">
                            Özet metin
                        </label>
                        <textarea
                            id="signature-intro"
                            className="signature-editor__textarea"
                            value={draft.intro}
                            onChange={(event) => handleFieldChange("intro", event.target.value)}
                            placeholder="Projenin kısa hikayesini yazın"
                        />
                    </div>

                    <div className="signature-editor__row">
                        <label className="signature-editor__label" htmlFor="signature-cta-label">
                            CTA Etiketi
                        </label>
                        <input
                            id="signature-cta-label"
                            className="signature-editor__input"
                            value={draft.ctaLabel}
                            onChange={(event) => handleFieldChange("ctaLabel", event.target.value)}
                            placeholder="Detaylı case talep et"
                        />
                    </div>

                    <div className="signature-editor__row">
                        <label className="signature-editor__label" htmlFor="signature-cta-url">
                            CTA URL
                        </label>
                        <input
                            id="signature-cta-url"
                            className="signature-editor__input"
                            value={draft.ctaUrl}
                            onChange={(event) => handleFieldChange("ctaUrl", event.target.value)}
                            placeholder="mailto:studio@modelerman.com"
                        />
                    </div>
                </fieldset>

                <fieldset className="signature-editor__fieldset">
                    <legend>Meta bilgileri</legend>
                    <div className="signature-editor__chips">
                        {(draft.meta ?? []).map((item, index) => (
                            <div key={`meta-${index}`} className="signature-editor__chip">
                                <div className="signature-editor__row">
                                    <label className="signature-editor__label" htmlFor={`meta-${index}`}>
                                        Satır {index + 1}
                                    </label>
                                    <input
                                        id={`meta-${index}`}
                                        className="signature-editor__input"
                                        value={item}
                                        onChange={(event) => handleMetaChange(index, event.target.value)}
                                        placeholder="Konum: Toronto Waterfront"
                                    />
                                </div>
                                <div className="signature-editor__chip-actions">
                                    <button
                                        type="button"
                                        className="signature-editor__chip-button"
                                        onClick={() => removeMeta(index)}
                                    >
                                        Sil
                                    </button>
                                </div>
                            </div>
                        ))}
                        <button type="button" className="signature-editor__chip-button" onClick={addMeta}>
                            Meta satırı ekle
                        </button>
                    </div>
                </fieldset>

                <fieldset className="signature-editor__fieldset">
                    <legend>Hikâye paragrafları</legend>
                    <div className="signature-editor__chips">
                        {(draft.body ?? []).map((item, index) => (
                            <div key={`body-${index}`} className="signature-editor__chip">
                                <div className="signature-editor__row">
                                    <label className="signature-editor__label" htmlFor={`body-${index}`}>
                                        Paragraf {index + 1}
                                    </label>
                                    <textarea
                                        id={`body-${index}`}
                                        className="signature-editor__textarea"
                                        value={item}
                                        onChange={(event) => handleBodyChange(index, event.target.value)}
                                        placeholder="Projenin ayrıntılı hikayesini yazın"
                                    />
                                </div>
                                <div className="signature-editor__chip-actions">
                                    <button
                                        type="button"
                                        className="signature-editor__chip-button"
                                        onClick={() => removeBody(index)}
                                    >
                                        Sil
                                    </button>
                                </div>
                            </div>
                        ))}
                        <button type="button" className="signature-editor__chip-button" onClick={addBody}>
                            Paragraf ekle
                        </button>
                    </div>
                </fieldset>

                <fieldset className="signature-editor__fieldset">
                    <legend>Timeline</legend>
                    <div className="signature-editor__chips">
                        {(draft.timeline ?? []).map((item, index) => (
                            <div key={`timeline-${index}`} className="signature-editor__chip">
                                <div className="signature-editor__row">
                                    <label className="signature-editor__label" htmlFor={`timeline-title-${index}`}>
                                        Başlık {index + 1}
                                    </label>
                                    <input
                                        id={`timeline-title-${index}`}
                                        className="signature-editor__input"
                                        value={item.title}
                                        onChange={(event) => handleTimelineChange(index, "title", event.target.value)}
                                        placeholder="Konsept & veri toplama"
                                    />
                                </div>
                                <div className="signature-editor__row">
                                    <label className="signature-editor__label" htmlFor={`timeline-desc-${index}`}>
                                        Açıklama
                                    </label>
                                    <textarea
                                        id={`timeline-desc-${index}`}
                                        className="signature-editor__textarea"
                                        value={item.description}
                                        onChange={(event) => handleTimelineChange(index, "description", event.target.value)}
                                        placeholder="Bu fazda yapılanlar"
                                    />
                                </div>
                                <div className="signature-editor__chip-actions">
                                    <button
                                        type="button"
                                        className="signature-editor__chip-button"
                                        onClick={() => removeTimeline(index)}
                                    >
                                        Sil
                                    </button>
                                </div>
                            </div>
                        ))}
                        <button type="button" className="signature-editor__chip-button" onClick={addTimeline}>
                            Timeline adımı ekle
                        </button>
                    </div>
                </fieldset>

                <fieldset className="signature-editor__fieldset">
                    <legend>Galeri</legend>
                    <div className="signature-editor__row signature-editor__row--inline">
                        <button
                            type="button"
                            className="signature-editor__button signature-editor__button--ghost"
                            onClick={() => setUploadPanelOpen(true)}
                        >
                            Fotoğraf yükleme paneli
                        </button>
                        {!supabaseEnabled && (
                            <span className="signature-editor__hint" style={{ margin: 0 }}>
                                VPS storage tanımlanınca bu panelden dosya yükleyebilirsiniz.
                            </span>
                        )}
                    </div>
                    <div className="signature-editor__chips">
                        {(draft.gallery ?? []).map((item, index) => (
                            <div key={`gallery-${index}`} className="signature-editor__chip">
                                <div className="signature-editor__row">
                                    <label className="signature-editor__label" htmlFor={`gallery-src-${index}`}>
                                        Görsel {index + 1} URL
                                    </label>
                                    <input
                                        id={`gallery-src-${index}`}
                                        className="signature-editor__input"
                                        value={item.src}
                                        onChange={(event) => handleGalleryChange(index, "src", event.target.value)}
                                        placeholder="https://..."
                                    />
                                </div>
                                <div className="signature-editor__row">
                                    <label className="signature-editor__label" htmlFor={`gallery-alt-${index}`}>
                                        Açıklama
                                    </label>
                                    <input
                                        id={`gallery-alt-${index}`}
                                        className="signature-editor__input"
                                        value={item.alt}
                                        onChange={(event) => handleGalleryChange(index, "alt", event.target.value)}
                                        placeholder="Render açıklaması"
                                    />
                                </div>
                                <div className="signature-editor__chip-actions">
                                    <button
                                        type="button"
                                        className="signature-editor__chip-button"
                                        onClick={() => removeGallery(index)}
                                    >
                                        Sil
                                    </button>
                                </div>
                            </div>
                        ))}
                        <button type="button" className="signature-editor__chip-button" onClick={addGallery}>
                            Galeri görseli ekle
                        </button>
                    </div>
                </fieldset>

                <fieldset className="signature-editor__fieldset">
                    <legend>Yayınlama</legend>
                    <div className="signature-editor__row">
                        <label className="signature-editor__checkbox">
                            <input
                                type="checkbox"
                                checked={draft.published}
                                onChange={() => handleFieldChange("published", !draft.published)}
                            />
                            Yayında olsun
                        </label>
                        {!supabaseEnabled && (
                            <p className="signature-editor__hint">
                                Supabase bağlantısı kurulmadan yayın durumu yalnızca bu oturumda tutulur.
                            </p>
                        )}
                    </div>
                </fieldset>
            </div>

            <footer className="signature-editor__actions">
                <div className="signature-editor__left">
                    <button
                        type="button"
                        className="signature-editor__button"
                        onClick={onReset}
                        disabled={saving || !hasChanges}
                    >
                        Sıfırla
                    </button>
                    <button type="button" className="signature-editor__button" onClick={onDuplicate} disabled={saving}>
                        Kopyala
                    </button>
                </div>
                <button
                    type="button"
                    className="signature-editor__button signature-editor__button--primary"
                    onClick={onSave}
                    disabled={saving || !hasChanges}
                >
                    {saving ? "Kaydediliyor..." : "Değişiklikleri kaydet"}
                </button>
            </footer>
            <AssetUploadPanel
                open={isUploadPanelOpen}
                onClose={() => setUploadPanelOpen(false)}
                supabaseEnabled={supabaseEnabled}
                onInsert={handleInsertAsset}
            />
        </aside>
    );
}
